import { useEffect, useRef, useState } from 'react'
import { useI18n } from '../i18n/context.js'
import { ApiError, newIdempotencyKey } from '../api/client.js'
import { deployPage, getPublicationStatus, getSite, publishSite } from '../api/endpoints.js'
import type { PublicationStatus } from '../api/endpoints.js'
import { deployProblem } from '../domain/pages.js'
import { resolveReason } from '../domain/reason.js'
import { Dialog } from '../ui/overlays.js'
import { ReasonNote } from '../ui/fields.js'
import { Banner, Button, ExternalButton, LinearProgress } from '../ui/primitives.js'

interface PublishDialogProps {
  siteId: string
  siteName?: string
  onClose: () => void
  /** Called once the publication succeeded, so the caller can refresh "last published". */
  onPublished?: () => void
  /** Publish only this page (`POST /pages/{id}/deploy`) instead of the whole site; the progress is the same publication. */
  page?: { id: string; version: number; title: string; path: string; removal?: boolean }
}

type Phase = 'confirm' | 'publishing' | 'done' | 'failed'

/**
 * "Pubblica sito": confirm, then follow the publication to its end.
 *
 * The site detail carries its aggregate version, so publication uses the
 * normal optimistic-lock path instead of provoking a 422 to discover state
 * the API already owns.
 */
export function PublishDialog({ siteId, siteName, onClose, onPublished, page }: PublishDialogProps) {
  const { t } = useI18n()
  const [phase, setPhase] = useState<Phase>('confirm')
  const [error, setError] = useState<string | null>(null)
  const [publicationId, setPublicationId] = useState<string | null>(null)
  const [publication, setPublication] = useState<PublicationStatus | null>(null)
  const [note, setNote] = useState('')
  const removal = page?.removal === true
  const fallbackReason = page ? t(removal ? 'polish.deploy.removeReason' : 'pages.deploy.reason', { path: page.path }) : t('reason.publish')
  const onPublishedRef = useRef(onPublished)
  onPublishedRef.current = onPublished

  const handleConfirm = async () => {
    setPhase('publishing')
    setError(null)
    try {
      if (page) {
        const result = await deployPage(page.id, { reason: resolveReason(note, fallbackReason), expectedVersion: page.version }, newIdempotencyKey())
        setPublicationId(result.publicationId)
      } else {
        const site = await getSite(siteId)
        const result = await publishSite(siteId, { reason: resolveReason(note, fallbackReason), expectedVersion: site.version }, newIdempotencyKey())
        setPublicationId(result.publicationId)
      }
    } catch (err) {
      // Three refusals of the single-page deploy have their own explanation; the rest shows the gateway's sentence.
      const problem = page && err instanceof ApiError ? deployProblem(err) : null
      setError(problem ? t(`pages.deploy.${problem}`) : err instanceof ApiError ? err.message : t('errors.generic'))
      setPhase('failed')
    }
  }

  useEffect(() => {
    if (phase !== 'publishing' || !publicationId) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const check = async () => {
      try {
        const next = await getPublicationStatus(siteId, publicationId)
        if (cancelled) return
        setPublication(next)
        if (next.status === 'succeeded') {
          setPhase('done')
          onPublishedRef.current?.()
        } else if (next.status === 'failed') {
          setError(next.failureReason ?? t('publish.failure'))
          setPhase('failed')
        } else timer = setTimeout(() => void check(), 1_000)
      } catch (err) {
        if (cancelled) return
        setError(err instanceof ApiError ? err.message : t('errors.generic'))
        setPhase('failed')
      }
    }
    void check()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [phase, publicationId, siteId, t])

  if (phase === 'confirm') {
    return <Dialog
      title={page ? t(removal ? 'polish.deploy.removeTitle' : 'pages.deploy.title') : t('publish.title')}
      icon={removal ? 'link_off' : 'rocket_launch'}
      onClose={onClose}
      actions={<>
        <Button variant="text" onClick={onClose}>{t('publish.cancel')}</Button>
        <Button variant="filled" icon={removal ? 'link_off' : 'rocket_launch'} onClick={() => void handleConfirm()} data-autofocus>{page ? t(removal ? 'polish.deploy.removeConfirm' : 'pages.deploy.confirm') : t('publish.confirm')}</Button>
      </>}
    >
      <p className="dialog-message">{page ? t(removal ? 'polish.deploy.removeMessage' : 'pages.deploy.message', { title: page.title }) : siteName ? t('publish.confirmMessageFor', { site: siteName }) : t('publish.confirmMessage')}</p>
      <ReasonNote value={note} onChange={setNote} fallback={fallbackReason}/>
    </Dialog>
  }

  return <Dialog
    title={page ? t(removal ? 'polish.deploy.removeTitle' : 'pages.deploy.title') : t('publish.title')}
    icon={removal ? 'link_off' : 'rocket_launch'}
    onClose={onClose}
    dismissible={phase !== 'publishing'}
    actions={phase === 'publishing' ? undefined : <>
      {phase === 'done' && publication?.deploymentUrl && <ExternalButton variant="outlined" href={publication.deploymentUrl}>{t('publish.openDeployment')}</ExternalButton>}
      <Button variant="filled" onClick={onClose} data-autofocus>{t('common.close')}</Button>
    </>}
  >
    {phase === 'publishing' && <div className="publish-progress" aria-live="polite">
      <p>{t(`publish.phase.${publication?.phase ?? 'planning'}`)}</p>
      <LinearProgress label={t('publish.inProgress')} value={publication && publication.totalFiles > 0 ? publication.completedFiles : undefined} max={Math.max(publication?.totalFiles ?? 1, 1)}/>
      <p className="muted">{t('publish.progress', { completed: publication?.completedFiles ?? 0, total: publication?.totalFiles ?? 0 })}</p>
    </div>}
    {phase === 'done' && <Banner tone="success" title={page ? t(removal ? 'polish.deploy.removeSuccess' : 'pages.deploy.success') : t('publish.success')}>{page ? t(removal ? 'polish.deploy.removeSuccessHelp' : 'pages.deploy.successHelp') : t('publish.successHelp')}</Banner>}
    {phase === 'failed' && <Banner tone="error" title={page ? t(removal ? 'polish.deploy.removeFailure' : 'pages.deploy.failure') : t('publish.failure')}>{error}</Banner>}
    {(phase === 'done' || phase === 'failed') && (publication?.warnings?.length ?? 0) > 0 && <Banner tone="warning" title={t('publish.warnings', { count: publication?.warnings.length ?? 0 })}>
      <ul className="plain-list">{publication?.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul>
    </Banner>}
  </Dialog>
}
